import { useRouter } from 'next/router'
import { useState } from 'react'

import dayjs from 'dayjs'
import Image from 'next/image'
import { useDispatch, useSelector } from 'react-redux'
import $sentry from '~/lib/sentry'
import { setCurrencyState } from '~/redux/slices/currencySlice'
import { AppDispatch, RootState } from '~/redux/store'
import { BusinessI } from '~/types'
import { GetCustomerShortNames, setCurrentBusiness } from '~/utils'
import { useAuth } from '../Auth/AuthProvider'
import { useGlobal } from '../Common/GlobalDataProvider'
import IconChevronRightOutline from '../Icon/IconChevronRightOutline'
import { SubscriptionIcon } from '../Subscription/SubscriptionIcon'
import { useSubscription } from '../Subscription/SubscriptionProvider'
import { PopupBusinessForm } from './PopupBusinessForm'

interface BusinessCardI {
    business: BusinessI
    type?: 'listing' | 'single'
    localeText: any
    index?: number
}
export const isValidImage = (img?: string | null) => {
    return typeof img === "string" && img.trim() !== "";
};
export const BusinessCard = ({
    business,
    type = 'single',
    localeText,
    index
}: BusinessCardI) => {
    const { toastProps, setToastProps } = useGlobal()
    const { openPaywall } = useSubscription()
    const { loadProfile, checkAccess } = useAuth()

    const myCurrency = useSelector(
        (state: RootState) => state.currency.currencySymbol
    )

    const router = useRouter()
    const [showBusinessForm, setShowBusinessForm] = useState(false)
    const dispatch: AppDispatch = useDispatch()

    const hasImage = isValidImage(business?.imageUrl)

    const hasSubscription =
        business.subscription &&
        !dayjs().isAfter(dayjs(business.subscription.endDate).local())

    const handleClick = () => {
        if (type === 'single') {
            if (checkAccess('changeBusinessProfile')) {
                setShowBusinessForm(true)
            }
            return
        }

        if (business.isDeleted) {
            setToastProps({
                ...toastProps,
                message: "This business does't longer exists.",
                type: 'error',
                open: true,
                wrapper: 'right-hand-side'
            })
            return
        }

        if (index !== 0) {
            const hasSubscription =
                business.subscription &&
                !dayjs().isAfter(dayjs(business.subscription.endDate).local())

            const isTrialActive =
                business.createdAt &&
                !dayjs().isAfter(dayjs(business.createdAt).add(14, 'day'))

            if (!hasSubscription && !isTrialActive) {
                openPaywall(Number(business.businessId))
                return
            }
        }

        setCurrentBusiness(Number(business.businessId))
        dispatch(
            setCurrencyState({
                currencySymbol: (business?.currency?.symbol ?? business?.currency?.symbol ?? business?.currencyId) as string
            })
        )

        router.push(`/business/dashboard`)
    }

    return (
        <>
            <div
                className="group relative flex cursor-pointer items-center gap-[14px] overflow-hidden rounded-[16px] border border-[#e8e6e1] bg-white p-[18px_20px] transition-all duration-[0.22s] hover:-translate-y-[2px] hover:border-transparent hover:shadow-[0_8px_30px_rgba(0,0,0,0.10),0_4px_12px_rgba(0,0,0,0.06)]"
                role="button"
                onClick={handleClick}
            >
                <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-[11px] overflow-hidden transition-transform duration-[0.22s] group-hover:scale-[1.08]">
                    {!hasImage ? (
                        <div className="bg-primary-50 flex items-center justify-center h-full w-full font-bold text-primary-600">
                            {GetCustomerShortNames(business?.name)}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full w-full overflow-hidden">
                            <Image
                                src={business.imageUrl!.trim()}
                                width={42}
                                height={42}
                                alt={business.name || 'Business Image'}
                                className="object-cover h-full w-full"
                                unoptimized
                                onError={() =>
                                    $sentry?.sendSentryEvent(
                                        'Error loading image: ' + business.imageUrl
                                    )
                                }
                            />
                        </div>
                    )}
                </div>

                <div className="min-w-0 flex-1 flex items-center gap-1.5">
                    <div className="truncate text-sm font-medium text-[#1a1916]">
                        {business?.name}
                    </div>
                    {hasSubscription && (
                        <div className="shrink-0 flex items-center justify-center w-[24px] h-[24px] [&>svg]:w-full [&>svg]:h-full">
                            <SubscriptionIcon />
                        </div>
                    )}
                </div>

                <div className="flex h-5 w-5 shrink-0 items-center justify-center text-[#a19e95] transition-transform duration-200 group-hover:translate-x-1 group-hover:text-[#1a1916]">
                    <IconChevronRightOutline />
                </div>
            </div>

            <PopupBusinessForm
                show={showBusinessForm}
                localeText={localeText}
                onSave={async () => {
                    await loadProfile()
                    setShowBusinessForm(false)
                }}
                onClose={() => setShowBusinessForm(false)}
                data={business}
            />
        </>
    )
}